/**
 * 🔐 Manage Passkeys — profile section
 *
 * Full lifecycle: enroll (OTP-gated), list, rename, delete.
 * Only surfaces the enroll flow when the browser supports platform auth.
 */
import { useEffect, useState } from "react";
import { Fingerprint, Loader2, Plus, Trash2, Check, X, Shield } from "lucide-react";
import { toast } from "sonner";
import {
  isPasskeySupported,
  isPlatformAuthenticatorAvailable,
  sendPasskeyOtp,
  enrollPasskey,
  listMyPasskeys,
  deletePasskey,
  renamePasskey,
} from "@/lib/passkeys";

function fmtDate(iso) {
  if (!iso) return "never";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function guessDefaultLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return "iPhone Face ID";
  if (/Mac OS X/.test(ua)) return "Mac Touch ID";
  if (/Android/.test(ua)) return "Android biometrics";
  if (/Windows/.test(ua)) return "Windows Hello";
  return "This device";
}

export default function ManagePasskeys() {
  const [supported, setSupported] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // enrollment wizard state
  const [step, setStep] = useState("idle"); // idle | otp-sent | ceremony | done
  const [otp, setOtp] = useState("");
  const [label, setLabel] = useState(guessDefaultLabel());
  const [busy, setBusy] = useState(false);
  const [otpTtl, setOtpTtl] = useState(10);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await listMyPasskeys();
      setRows(data?.passkeys || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load passkeys.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const ok = isPasskeySupported() && (await isPlatformAuthenticatorAvailable());
      setSupported(ok);
    })();
    refresh();
  }, []);

  // 🔗 If the URL landed here with #face-id (from the PasskeyNudge CTA),
  // scroll the section into view once mounted.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#face-id") {
      const t = setTimeout(() => {
        document.getElementById("face-id")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, []);

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await sendPasskeyOtp();
      setOtpTtl(r?.ttl_minutes || 10);
      setStep("otp-sent");
      toast.success("Check your email for the 6-digit code.");
    } catch (e) {
      toast.error(e?.message || "Failed to send code.");
    } finally {
      setBusy(false);
    }
  };

  const doEnroll = async () => {
    if (otp.length < 4) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    setStep("ceremony");
    try {
      await enrollPasskey({ otp, label: (label || "Passkey").trim() });
      setStep("done");
      toast.success("Passkey enrolled 🎉");
      setOtp("");
      await refresh();
      setTimeout(() => setStep("idle"), 800);
    } catch (e) {
      toast.error(e?.message || "Enrollment failed.");
      setStep("otp-sent");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (credId, name) => {
    if (!window.confirm(`Remove "${name}" passkey? You'll need your password to sign in on that device.`)) return;
    try {
      await deletePasskey(credId);
      toast.success("Passkey removed.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    }
  };

  const doRename = async (credId, oldName) => {
    const next = window.prompt("New label for this passkey:", oldName || "Passkey");
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === oldName) return;
    try {
      await renamePasskey(credId, trimmed);
      toast.success("Renamed.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Rename failed.");
    }
  };

  return (
    <section
      id="face-id"
      data-testid="manage-passkeys-section"
      className="border-2 border-[#D4AF37]/60 bg-[#0A0A0A] p-5 mb-6 scroll-mt-20"
    >
      <div className="flex items-center gap-3 mb-2">
        <Fingerprint size={22} className="text-[#D4AF37]" />
        <h3 className="font-display text-xl text-white">Face ID & Passkeys</h3>
      </div>
      <p className="text-xs text-neutral-400 leading-relaxed mb-4">
        Skip the password on trusted devices. Enroll once, then sign in with
        Face ID, Touch ID, Windows Hello, or Android biometrics. You can
        register a passkey for every device you own.
      </p>

      {/* Existing passkeys */}
      {loading ? (
        <div className="flex items-center gap-2 text-neutral-400 text-sm" data-testid="passkeys-loading">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-neutral-500 text-xs italic mb-4" data-testid="passkeys-empty">
          No passkeys enrolled yet.
        </div>
      ) : (
        <ul className="space-y-2 mb-5" data-testid="passkeys-list">
          {rows.map((p) => (
            <li
              key={p.credential_id}
              className="flex items-center gap-3 border border-zinc-800 bg-zinc-900/50 px-3 py-2"
              data-testid={`passkey-row-${p.credential_id}`}
            >
              <Shield size={16} className="text-[#D4AF37] shrink-0" />
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => doRename(p.credential_id, p.name)}
                  className="text-sm text-white font-semibold truncate block hover:text-[#D4AF37] text-left w-full"
                  data-testid={`passkey-rename-${p.credential_id}`}
                  title="Click to rename"
                >
                  {p.name}
                </button>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Added {fmtDate(p.created_at)}
                  {p.last_used_at ? <> · Used {fmtDate(p.last_used_at)}</> : null}
                  {p.backed_up ? <> · ☁️ Synced</> : null}
                </div>
              </div>
              <button
                onClick={() => doDelete(p.credential_id, p.name)}
                className="text-red-400 hover:text-red-300 p-1"
                data-testid={`passkey-delete-${p.credential_id}`}
                aria-label={`Delete ${p.name}`}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Enrollment wizard */}
      {!supported ? (
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/40 px-3 py-2" data-testid="passkey-unsupported">
          This device doesn&apos;t support platform passkeys. Try on iPhone, Android, or a laptop with a fingerprint reader.
        </div>
      ) : step === "idle" ? (
        <button
          onClick={sendCode}
          disabled={busy}
          className="btn-omni btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="passkey-start-enroll"
        >
          {busy ? <><Loader2 size={14} className="animate-spin" /> Sending code…</> : <><Plus size={16} /> Add a New Passkey</>}
        </button>
      ) : (
        <div className="space-y-3" data-testid="passkey-otp-form">
          <div className="text-xs text-neutral-300 bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-3 py-2">
            We emailed you a 6-digit code. Enter it below, then approve the Face ID prompt. Code expires in {otpTtl} minutes.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
              6-digit code
            </label>
            <input
              className="input-omni text-center tracking-[0.5em] text-lg"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              data-testid="passkey-otp-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
              Device label
            </label>
            <input
              className="input-omni"
              placeholder="iPhone Face ID"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={60}
              data-testid="passkey-label-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={doEnroll}
              disabled={busy || otp.length < 6}
              className="btn-omni btn-red flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="passkey-confirm-enroll"
            >
              {busy ? (
                <><Loader2 size={14} className="animate-spin" /> Enrolling…</>
              ) : (
                <><Check size={14} /> Verify & Enroll</>
              )}
            </button>
            <button
              onClick={() => { setStep("idle"); setOtp(""); }}
              disabled={busy}
              className="btn-omni btn-ghost px-4 flex items-center justify-center"
              data-testid="passkey-cancel-enroll"
              aria-label="Cancel enrollment"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
